import { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import PasswordResetDialog from "./PasswordResetDialog.tsx";
import { getJaDisplayName } from "components/userUtil.ts";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [openPasswordResetDialog, setOpenPasswordResetDialog] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is already logged in, redirect to /mypage
        navigate("/mypage");
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);


  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setLoading(false);
      navigate("/mypage");
    } catch (error: any) {
      let errorMessage = "ログインに失敗しました。";
      if (error.code === "auth/invalid-email") {
        errorMessage = "無効なメールアドレスです。";
      } else if (error.code === "auth/user-disabled") {
        errorMessage = "このアカウントは無効化されています。";
      } else if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/wrong-password"
      ) {
        errorMessage = "メールアドレスまたはパスワードが間違っています。";
      }
      setError(errorMessage);
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        ログイン
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        メールアドレスとパスワードを入力してください
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <Grid container spacing={2} sx={{ mb: 5 }}>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={getJaDisplayName("email")}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={getJaDisplayName("password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid size={12}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "ログイン中..." : "ログイン"}
            </Button>
          </Grid>
        </Grid>
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Link
            variant="body2"
            onClick={() => setOpenPasswordResetDialog(true)}
            sx={{ cursor: "pointer" }}
          >
            パスワードをお忘れの方はこちら
          </Link>
          <Link
            component={RouterLink}
            variant="body2"
            to="/register"
            sx={{ cursor: "pointer" }}
          >
            新規会員登録はこちら
          </Link>
        </Box>
      </Box>

      <PasswordResetDialog
        open={openPasswordResetDialog}
        onClose={() => setOpenPasswordResetDialog(false)}
      />
    </Container>
  );
}
